import { useTranslation } from 'react-i18next';
import { useCallback, useEffect, useState } from 'react';
import DashboardSimple from './simple';
import DashboardDetailed from './detailed';
import Label from '../../../components/atoms/text/label';
import Select from '../../../components/atoms/select';
import { EditableChangeEvent } from '../../../utils/types/editable';
import Button from '../../../components/atoms/button';
import InputCalendar from '../../../components/molecules/input-calendar';
import SVGIcon from '../../../components/atoms/icons';
import RadioOption from '../../../components/atoms/radio';
import { useRouteLoaderData } from 'react-router-dom';
import Modal from '../../../components/organisms/modal';
import ContainerService from '../../../services/Container.service';
import { HomeLoaderData } from '../../../types/homeLoaderData';

export interface IDashboardYard {
	name: string;
	alerts: number;
	monitoredCount: number;
	total: number;
	sockets: number;
	cold: number;
	frozen: number;
}

export interface IDashboard {
	containersTotalMonitored: number;
	monitored: number;
	alerts: number;
	sockets: string;
	frozen: number;
	cold: number;
	consumption: string;
	efficiency: string;
	yards: IDashboardYard[];
	consumptionData: {
		label: string;
		frozen: number;
		cold: number;
	}[];
	status: {
		alert: number;
		critical: number;
		operational: number;
	};
	value: string;
	monthlyCost: {
		value: string;
		rate: number;
	};
	frozenCost: string;
	coldCost: string;
	maintenances: {
		name: string;
		occurrencies: number;
	}[];
}

const mockData: IDashboard = {
	containersTotalMonitored: 0,
	monitored: 0,
	alerts: 0,
	sockets: '0/0',
	frozen: 0,
	cold: 0,
	consumption: '0 kW/h',
	efficiency: '0%',
	status: {
		critical: 0,
		alert: 0,
		operational: 0,
	},
	value: 'R$ 0,00',
	monthlyCost: {
		value: 'R$ 0,00',
		rate: 0,
	},
	frozenCost: 'R$ 0,00',
	coldCost: 'R$ 0,00',
	maintenances: [],
	yards: [],
	consumptionData: [],
};

export type DashboardMode = 'simple' | 'detailed';

interface ReportFilters {
	ships: string[];
	containers: string[];
	travelCodes: string[];
}

const Dashboard = ({ idCliente }: { idCliente: string }) => {
	const { t } = useTranslation();

	const { user, accountId } = useRouteLoaderData('home') as HomeLoaderData;

	const [isModalVisible, setIsModalVisible] = useState<boolean>(false);
	const [reportFilters, setReportFilters] = useState<ReportFilters>({
		ships: [t('misc.all')],
		containers: [t('misc.all')],
		travelCodes: [t('misc.all')],
	});

	const [filters, setFilters] = useState({
		ship: t('misc.all'),
		container: t('misc.all'),
		travelCode: t('misc.all'),
	});

	const [data, setData] = useState<IDashboard>(() => {
		const dataSaved = localStorage.getItem(`dashboard:${idCliente}`);
		if (!dataSaved) return mockData;
		const objParsed = JSON.parse(dataSaved);
		if (!objParsed) return mockData;
		return objParsed;
	});

	const onModalClose = () => setIsModalVisible(false);

	const modeOptions = {
		simple: t('dashboard.simple'),
		detailed: t('dashboard.detailed'),
	};
	const [mode, setMode] = useState<DashboardMode>('simple');
	const [carregando, setCarregando] = useState<boolean>(true);

	const periodOptions = [
		t('date.today'),
		t('date.monthly'),
		t('date.yearly'),
		t('date.custom'),
	];
	const [period, setPeriod] = useState<string>(periodOptions[0]);

	const [initialDate, setInitialDate] = useState<Date>(new Date());
	const [finalDate, setFinalDate] = useState<Date>(new Date());

	const getReportFilters = useCallback(async () => {
		const response: ReportFilters = await ContainerService.getInfoByEmail(
			user.email
		);
		if (!response) return;
		setReportFilters(() => ({
			ships: [t('misc.all'), ...response.ships],
			containers: [t('misc.all'), ...response.containers],
			travelCodes: [t('misc.all'), ...response.travelCodes],
		}));
	}, []);

	useEffect(() => {
		getReportFilters();
	}, [getReportFilters]);

	const handlePeriod = (e: EditableChangeEvent) => {
		setPeriod(e.target.value);
	};

	const handleMode = (e: React.ChangeEvent<HTMLInputElement>) => {
		const newMode = e.target.value as string;
		if (newMode === t('dashboard.simple')) {
			setMode('simple');
			return;
		}
		setMode('detailed');
	};

	const handleInitialDate = (date: Date) => {
		setInitialDate(date);
	};

	const handleFinalDate = (date: Date) => {
		setFinalDate(date);
	};

	const handleFilterChange = (e: EditableChangeEvent) => {
		setFilters((prev) => ({
			...prev,
			[e.target.name]: e.target.value,
		}));
	};

	const getDates = () => {
		let dateBegin: Date, dateEnd: Date;

		const today = new Date();

		if (period === t('date.today')) {
			// Dia atual
			dateBegin = new Date(today);
			dateBegin.setHours(0, 0, 0, 0);
			dateEnd = new Date(today);
			dateEnd.setHours(23, 59, 59, 999);
		} else if (period === t('date.monthly')) {
			// Relatório mensal
			dateBegin = new Date(
				Date.UTC(
					today.getUTCFullYear(),
					today.getUTCMonth(),
					1,
					0,
					0,
					0
				)
			);
			dateEnd = new Date(
				Date.UTC(
					today.getUTCFullYear(),
					today.getUTCMonth() + 1,
					0, // Último dia do mês atual
					23,
					59,
					59
				)
			);
		} else if (period === t('date.yearly')) {
			// Relatório anual
			dateBegin = new Date(
				Date.UTC(
					today.getUTCFullYear(),
					0, // Janeiro
					1,
					0,
					0,
					0
				)
			);
			dateEnd = new Date(
				Date.UTC(
					today.getUTCFullYear(),
					11, // Dezembro
					31,
					23,
					59,
					59
				)
			);
		} else {
			// Período customizado
			if (!initialDate || !finalDate || finalDate < initialDate) return;

			dateBegin = new Date(initialDate);
			dateEnd = new Date(finalDate);

			// Garante que a hora está no final do dia para dateEnd
			dateEnd.setHours(23, 59, 59, 999);
		}

		return [dateBegin, dateEnd];
	};

	const loadData = useCallback(async () => {
		const [dateBegin, dateEnd] = getDates() || [initialDate, finalDate];

		setCarregando(true);
		try {
			const response: Partial<IDashboard> =
				await ContainerService.getDashboardInfo(
					dateBegin,
					dateEnd,
					idCliente
				);
			if (!response) return;
			setData((prev) => ({ ...prev, ...response }));
			if (period == t('date.today'))
				localStorage.setItem(`dashboard:${idCliente}`, JSON.stringify(response));
		} finally {
			setCarregando(false);
		}
	}, [initialDate, finalDate, period, idCliente]);

	useEffect(() => {
		loadData();
	}, [loadData]);

	const handleReports = async () => {
		sessionStorage.removeItem('report');
		const [dateBegin, dateEnd] = getDates() || [initialDate, finalDate];

		const params = new URLSearchParams({
			ship: filters.ship,
			travelCode: filters.travelCode,
			container: filters.container,
			accountId,
			dateBegin: dateBegin.toISOString(),
			dateEnd: dateEnd.toISOString(),
			_t: Date.now().toString(),
		});

		// Navegar para a página de reports com os parâmetros
		const reportUrl = `/reports/data?${params.toString()}`;

		// Abrir em nova aba
		window.open(reportUrl, '_blank');
	};

	return (
		<div className="flex flex-col gap-3">
			<div className="flex flex-wrap justify-between gap-3">
				<div className="flex flex-wrap gap-3">
					<Select
						className="w-48"
						name="period"
						value={period}
						placeholder={t('date.period')}
						onChange={handlePeriod}
						options={periodOptions}
					/>
					{period === t('date.custom') && (
						<>
							<InputCalendar
								containerClassName="w-40"
								date={initialDate}
								placeholder={t('date.initial')}
								onDateChange={handleInitialDate}
							/>
							<InputCalendar
								containerClassName="w-40"
								date={finalDate}
								placeholder={t('date.final')}
								onDateChange={handleFinalDate}
							/>
						</>
					)}
					<Button onClick={() => setIsModalVisible(true)}>
						<SVGIcon
							className="stroke-white h-4 w-auto"
							icon="Download"
						/>
						{t('dashboard.export_report')}
					</Button>
				</div>
				{user.type === 'User' && (
					<div className="flex gap-3 items-center">
						<Label className="text-nowrap">
							{t('dashboard.view_mode')}:{' '}
						</Label>

						<div className="flex gap-2">
							<RadioOption
								id="viewSimple"
								name="view"
								label={modeOptions['simple']}
								value={modeOptions['simple']}
								checked={mode === 'simple'}
								onChange={handleMode}
							/>
							<RadioOption
								id="dayStatePM"
								name="view"
								label={modeOptions['detailed']}
								value={modeOptions['detailed']}
								checked={mode === 'detailed'}
								onChange={handleMode}
							/>
						</div>
					</div>
				)}
			</div>
			<DashboardSimple data={data} mode={mode} carregando={carregando} />
			{user.type === 'User' && (
				<DashboardDetailed data={data} mode={mode} carregando={carregando} />
			)}
			<Modal
				className="flex gap-4"
				isModalVisible={isModalVisible}
				onClose={onModalClose}
				title={t('dashboard.export_report')}
			>
				<Select
					label={t('port_management.ship')}
					options={reportFilters.ships}
					value={filters.ship}
					name="ship"
					onChange={handleFilterChange}
				/>
				<Select
					label={t('movement.travel_code')}
					options={reportFilters.travelCodes}
					value={filters.travelCode}
					name="travelCode"
					onChange={handleFilterChange}
				/>
				<Select
					label={t('port_management.container')}
					options={reportFilters.containers}
					value={filters.container}
					name="container"
					onChange={handleFilterChange}
				/>
				<div className="flex items-end justify-end">
					<Button className="w-fit" onClick={handleReports}>
						<SVGIcon
							className="stroke-white h-4 w-auto"
							icon="Download"
						/>
						{t('misc.save')}
					</Button>
				</div>
			</Modal>
		</div>
	);
};

export default Dashboard;
